/**
 * Self-service account panel: read-only identity (username / email / roles /
 * admin) plus a change-password form. Used by every signed-in user.
 */
import { useState } from 'react'
import ShellModal, { shellInput, shellBtn } from './ShellModal'
import type { ShellApi, ShellIdentity, ShellNotify } from './types'
import { MFA_LIGHT_VARS, TwoFactorPanel } from './mfa'

export default function AccountModal({
  open, onClose, identity, api, notify,
}: {
  open: boolean
  onClose: () => void
  identity: ShellIdentity
  api: ShellApi
  /** Host toast adapter; without one, success shows as inline text instead. */
  notify?: ShellNotify
}) {
  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const reset = () => { setOldPw(''); setNewPw(''); setConfirmPw(''); setErr(null); setOk(false) }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null); setOk(false)
    if (newPw.length < 8) { setErr('New password must be at least 8 characters.'); return }
    if (newPw !== confirmPw) { setErr('New passwords do not match.'); return }
    setBusy(true)
    try {
      await api.changePassword(oldPw, newPw)
      if (notify) {
        // Toast-capable host (the platform): confirm via toast and close.
        notify.success('Password changed')
        reset()
        onClose()
      } else {
        // No notifier (standalone runtime): keep the panel open and confirm
        // inline, matching the runtime's previous account dialog.
        setOldPw(''); setNewPw(''); setConfirmPw('')
        setOk(true)
      }
    } catch (e2: unknown) {
      setErr(extractError(e2) || 'Could not change password')
    } finally {
      setBusy(false)
    }
  }

  const appRoles = identity.roles.filter((r) => r !== 'admin')

  return (
    <ShellModal open={open} onClose={() => { reset(); onClose() }} title="My account">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <section>
          <Row label="Username" value={identity.username} />
          {identity.email ? <Row label="Email" value={identity.email} /> : null}
          <Row
            label="Access"
            value={identity.isAdmin
              ? 'Administrator'
              : (appRoles.length ? appRoles.join(', ') : 'Standard user')}
          />
          {appRoles.length > 0 && identity.isAdmin && (
            <Row label="Profiles" value={appRoles.join(', ')} />
          )}
        </section>

        <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '2px 0' }} />

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9625rem' }}>Change password</div>
          <input style={shellInput} type="password" placeholder="Current password"
            autoComplete="current-password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} />
          <input style={shellInput} type="password" placeholder="New password (min 8 chars)"
            autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          <input style={shellInput} type="password" placeholder="Confirm new password"
            autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
          {err && <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{err}</div>}
          {ok && <div style={{ color: '#16a34a', fontSize: '0.875rem' }}>Password changed.</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
            <button type="button" style={shellBtn('ghost')} onClick={() => { reset(); onClose() }}>Cancel</button>
            <button type="submit" style={{ ...shellBtn('primary'), opacity: busy || !oldPw || !newPw ? 0.6 : 1 }}
              disabled={busy || !oldPw || !newPw}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>

        {api.mfa && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '2px 0' }} />
            <div style={{ fontWeight: 700, fontSize: '0.9625rem' }}>Two-factor sign-in</div>
            <div style={MFA_LIGHT_VARS}><TwoFactorPanel api={api.mfa} notify={notify} /></div>
          </>
        )}
      </div>
    </ShellModal>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '4px 0', fontSize: '0.9375rem' }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

export function extractError(e: unknown): string {
  // response.data.detail envelope (platform hosts) …
  const envelopeDetail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  if (typeof envelopeDetail === 'string' && envelopeDetail) return envelopeDetail
  // … or a fetch-client error carrying the server `detail` directly (the
  // standalone runtime's ApiError).
  const detail = (e as { detail?: unknown })?.detail
  if (typeof detail === 'string' && detail) return detail
  return ''
}
